'use client';

import {
  UserCredential,
  signInWithPopup,
  GoogleAuthProvider,
  getAuth,
  signInWithCustomToken,
  User,
  onAuthStateChanged,
} from 'firebase/auth';
import { app } from '@/config/firebaseConfig';
import { useUserInfo } from '@/provider/UserInfoProvider';
import { useCallback, useEffect, useState } from 'react';
import {
  initUserEndpoint,
  sendCodeEndpoint,
  getCustomTokenEndpoint,
} from '@/config/endpoints';
import { toast } from 'sonner';
import { kazeFetch } from '@/utils/kazeFetch';
import { reportLogin } from '@/posthog/reportLogin';
import { getInviteInfo } from '@/ueshook/useInviteCode';

const { invite } = getInviteInfo();

type LoginWay = 'google-login' | 'email-login';

const ErrorTipMap = {
  CODE_SEND_LIMIT_ERROR: 'Please wait 30 seconds before requesting a new code',
  CODE_NOT_EXIST_ERROR:
    'The verification code is invalid, Please request a new code',
  CODE_EXPIRED_ERROR:
    'The verification code has expired, Please request a new code',
};

const useGlobalSignup = ({
  setModalVisible,
  signupSuccess,
}: {
  setModalVisible?: (open: boolean) => void;
  signupSuccess?: () => void;
}) => {
  const { setUserIdentity } = useUserInfo();
  const [sendCodePending, setSendCodePending] = useState(false);
  const [isCustomTokenPending, setIsCustomTokenPending] = useState(false);
  const [
    isLoginWithCustomTokenPending,
    setIsLoginWithCustomTokenPendingPending,
  ] = useState(false);

  const handleAuthSuccess = useCallback(
    async (user: User, loginWay: LoginWay) => {
      if (!user.isAnonymous) setUserIdentity(3);

      // 上报
      reportLogin(user, loginWay);
    },
    [setUserIdentity]
  );

  const handleAuthError = async (error: Error) => {
    console.error('Failed to initialize user:', error);
    const auth = getAuth();
    await auth.signOut();
    toast.error('Failed to complete sign in. Please try signing in again.');
  };

  const signInSuccessWithAuthResult = (
    authResult: UserCredential,
    loginWay: LoginWay
  ) => {
    const user = authResult.user;
    user
      .getIdToken()
      .then(() =>
        kazeFetch(initUserEndpoint, {
          method: 'POST',
          body: JSON.stringify({
            invite_code: invite.invite_code || null,
          }),
        })
      )
      .then(() => {
        handleAuthSuccess(user, loginWay);
      })
      .catch(handleAuthError);
  };

  // goole signup or login
  const loginViaGoogle = async () => {
    const auth = getAuth(app);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      const authResult = await signInWithPopup(auth, provider);
      setModalVisible?.(false);
      signupSuccess?.();
      signInSuccessWithAuthResult(authResult, 'google-login');
    } catch (error) {}
  };

  const sendCode = async (email: string) => {
    try {
      setSendCodePending(true);
      await kazeFetch(sendCodeEndpoint, {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    } catch (error: any) {
      console.error('send code fail', error);
      const code = (error as any)?.code;
      throw new Error(
        code
          ? ErrorTipMap[code as keyof typeof ErrorTipMap]
          : error?.message
            ? error.message
            : 'Send code fail'
      );
    } finally {
      setSendCodePending(false);
    }
  };

  const getCustomToken = async (email: string, code: string) => {
    try {
      if (isCustomTokenPending) return;
      setIsCustomTokenPending(true);
      const data = await kazeFetch(getCustomTokenEndpoint, {
        method: 'POST',
        body: JSON.stringify({ email, code }),
      });
      return data.custom_token;
    } catch (error: any) {
      console.error('get custom token fail', error);
      const code = error?.code;
      throw new Error(
        code
          ? ErrorTipMap[code as keyof typeof ErrorTipMap]
          : error?.message || 'Get custom token fail'
      );
    } finally {
      setIsCustomTokenPending(false);
    }
  };

  const loginViaCustomToken = async (token: string) => {
    const auth = getAuth(app);
    try {
      setIsLoginWithCustomTokenPendingPending(true);
      const authResult = await signInWithCustomToken(auth, token);
      setModalVisible?.(false);
      signupSuccess?.();
      signInSuccessWithAuthResult(authResult, 'email-login');
    } catch (error: any) {
      console.error('login with custom token fail', error);
      throw new Error(`login with custom token fail: ${error.message}`);
    } finally {
      setIsLoginWithCustomTokenPendingPending(false);
    }
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(getAuth(app), async (user) => {
      if (user && !user.isAnonymous) setModalVisible?.(false);
    });

    return () => unsubscribeAuth();
  }, [setModalVisible]);

  return {
    loginViaGoogle,
    sendCode,
    sendCodePending,
    getCustomToken,
    isCustomTokenPending,
    loginViaCustomToken,
    isLoginWithCustomTokenPending,
  };
};

export default useGlobalSignup;
